import { POLLING_INTERVAL_MS, useImmediateOutreachStore } from './immediate-outreach.store';
import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import JobService from '@/core/shared/job/job.service';
import MeService from '@/core/shared/me/me.service';

export default class ImmediateOutreachService {
  public readonly store = useImmediateOutreachStore();
  private conversationIndexService: ConversationIndexService;
  private jobService = new JobService();
  private meService = new MeService();

  constructor(conversationIndexService?: ConversationIndexService) {
    // Allow passing in an existing instance or create a new one
    this.conversationIndexService = conversationIndexService || new ConversationIndexService();
  }

  /**
   * Enables immediate outreach for a specific project
   */
  public enableForProject(projectId: number): void {
    this.store.enabledProjectIds.add(projectId);
  }

  /**
   * Disables immediate outreach for a specific project
   */
  public disableForProject(projectId: number): void {
    this.store.enabledProjectIds.delete(projectId);
  }

  /**
   * Manually starts the immediate outreach animation and polling
   * This should be called when specific conditions are met (e.g., job status changes to live with copilot)
   */
  public startImmediateOutreach(): void {
    // Don't start if already polling
    if (this.store.pollingActive) {
      return;
    }

    // Start polling
    this.startPollingHeaders();
  }

  /**
   * Starts polling for new conversation headers and job summaries
   * Polls every 60 seconds (1 minute) as configured in the store
   */
  private startPollingHeaders(): void {
    // Clear any existing timeout
    if (this.store.pollingInterval) {
      window.clearTimeout(this.store.pollingInterval);
    }

    // Mark polling as active
    this.store.pollingActive = true;

    // Set up recursive polling with setTimeout to prevent overlapping executions
    const poll = async (): Promise<void> => {
      await this.pollForUpdates();
      // Only schedule next poll if still active
      if (this.store.pollingActive) {
        this.store.pollingInterval = window.setTimeout(poll, POLLING_INTERVAL_MS);
      }
    };

    // Start the first poll immediately
    poll();
  }

  /**
   * Performs the actual polling for both headers and job summaries
   */
  private async pollForUpdates(): Promise<void> {
    // Mid-switch the search still holds the old job's ids, so a poll would list them under the new URL.
    if (this.conversationIndexService.isSwitchingJob) return;

    try {
      // Get the job IDs to poll for
      const jobIds = this.conversationIndexService.conversationIndexSearch.jobIds;
      const employerId = this.meService.employer?.id;

      // Create promises for both polling operations
      const promises: Promise<void | unknown>[] = [
        // Poll for new conversation headers
        this.conversationIndexService.fetchConversationIndexes(true),
      ];

      // If we have job IDs and employer ID, also poll for job summaries
      if (jobIds.length > 0 && employerId) {
        promises.push(this.jobService.fetchJobs(jobIds, employerId));
      }

      // Execute both polling operations in parallel (using allSettled so one failure doesn't affect the other)
      await Promise.allSettled(promises);

      // Check if we now have candidates
      if (this.conversationIndexService.conversationIndexes.length > 0) {
        // Stop animation when first candidate appears
        this.stopImmediateOutreach();
      }
    } catch (error) {
      ErrorService.captureException(error);
      // Continue polling even if there's an error
    }
  }

  /**
   * Manually stops the immediate outreach animation and polling
   */
  public stopImmediateOutreach(): void {
    // Clear the polling timeout
    if (this.store.pollingInterval) {
      window.clearTimeout(this.store.pollingInterval);
      this.store.pollingInterval = null;
    }

    // Set polling flag to false
    this.store.pollingActive = false;
  }
}
